import { Checkbox, DatePicker, message, Popover, Tooltip } from "antd";
import fr_FR from "antd/lib/date-picker/locale/fr_FR";
import dayjs from "dayjs";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { ReactComponent as NextIcon } from "../../../assets/img/icons/next.svg";
import { ReactComponent as PrevIcon } from "../../../assets/img/icons/prev.svg";
import sigmaIcon from "../../../assets/img/icons/sigma.svg";
import {
  handleActiveSynthesis,
  handleEndMonthSynthesis,
  handleSelectedMonthsSynthesis,
  handleStartMonthSynthesis,
} from "../../../data/slices/layout";
import { updateProject } from "../../../data/slices/project";
import { adjustCalendarYears } from "../../../utilities/adjustCalendarYears";
import { displayValidationErrors } from "../../../utilities/noticeDisplayer";
import {
  formatMonthRange,
  generateMonthList,
  parseDate,
} from "../../../utilities/filters";
import {
  formatFiscalYearEndLabel,
  getExerciceBounds,
} from "../../../utilities/periodPresets";
import { renderFiscalYearEndPickerPanel } from "../../../utilities/fiscalYearPickerPanel";

const SynthesePopover = ({ mode, isSidebarClosed, buttonName }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isListing = location.pathname.includes("listing");
  const isGraphPage = location.pathname.includes("graph");
  const isDisabled = isListing || isGraphPage;

  const { project } = useSelector((state) => state.project);
  const { selectedMonths, selectedMonthsSynthesis } = useSelector(
    (state) => state.layout
  );

  const [popoverVisible, setPopoverVisible] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [exerciceOpen, setExerciceOpen] = useState(false);
  const [defaultRangeOpen, setDefaultRangeOpen] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [savingExercice, setSavingExercice] = useState(false);
  const [savingDefaultRange, setSavingDefaultRange] = useState(false);

  const fiscalYearEnd = project?.fiscalYearEnd || null;

  const currentSyntheseRange = [
    selectedMonthsSynthesis?.map((month) => parseDate(month))[0],
    selectedMonthsSynthesis?.map((month) => parseDate(month))[
      selectedMonthsSynthesis.length - 1
    ],
  ];

  const displayedPeriodRange =
    selectedMonths?.length > 0
      ? [
          parseDate(selectedMonths[0]),
          parseDate(selectedMonths[selectedMonths.length - 1]),
        ]
      : null;

  const [draftRange, setDraftRange] = useState(currentSyntheseRange);
  const [defaultDraftRange, setDefaultDraftRange] = useState(null);
  const [valueBtnWidth, setValueBtnWidth] = useState(null);
  const popoverContentRef = useRef(null);
  const manualPeriodBtnRef = useRef(null);
  const draftRangeRef = useRef(draftRange);
  const appliedFromCalendarRef = useRef(false);

  useEffect(() => {
    setDraftRange(currentSyntheseRange);
    draftRangeRef.current = currentSyntheseRange;
  }, [selectedMonthsSynthesis]);

  useLayoutEffect(() => {
    if (!popoverVisible) return undefined;

    const syncValueBtnWidth = () => {
      const root = popoverContentRef.current;
      if (!root) return;

      root.style.removeProperty("--synthese-value-btn-width");

      const buttons = root.querySelectorAll(
        ".period-affichee-popover__header .period-affichee-current-btn, .period-affichee-synthese-presets .period-affichee-current-btn"
      );

      let maxWidth = 0;
      buttons.forEach((btn) => {
        maxWidth = Math.max(maxWidth, Math.ceil(btn.getBoundingClientRect().width));
      });

      if (maxWidth > 0) {
        root.style.setProperty("--synthese-value-btn-width", `${maxWidth}px`);
        setValueBtnWidth(maxWidth);
      }
    };

    const frame = requestAnimationFrame(syncValueBtnWidth);
    window.addEventListener("resize", syncValueBtnWidth);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncValueBtnWidth);
    };
  }, [
    popoverVisible,
    currentSyntheseRange,
    displayedPeriodRange,
    fiscalYearEnd,
    project?.syntheseDateStart,
    project?.syntheseDateEnd,
  ]);

  const savedSyntheseRange =
    project?.syntheseDateStart && project?.syntheseDateEnd
      ? [
          dayjs(project.syntheseDateStart, "YYYY-MM"),
          dayjs(project.syntheseDateEnd, "YYYY-MM"),
        ]
      : null;

  const applySynthesePeriod = async (
    dates,
    { persistDefault = false, closePopover = true } = {}
  ) => {
    if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) return;

    dispatch(handleStartMonthSynthesis(dates[0]));
    dispatch(handleEndMonthSynthesis(dates[1]));
    dispatch(
      handleSelectedMonthsSynthesis(generateMonthList(dates[0], dates[1]))
    );
    dispatch(handleActiveSynthesis(true));

    if (persistDefault && project?.id) {
      const result = await dispatch(
        updateProject({
          id: Number(project.id),
          syntheseDateStart: dayjs(dates[0]).format("YYYY-MM"),
          syntheseDateEnd: dayjs(dates[1]).format("YYYY-MM"),
        })
      );
      if (result.type === "project/updateProject/rejected") {
        displayValidationErrors(result?.payload || {});
      } else {
        message.success('Période de synthèse par défaut enregistrée');
      }
    }

    setRangeOpen(false);
    setExerciceOpen(false);
    setDefaultRangeOpen(false);
    setSaveAsDefault(false);

    if (closePopover) {
      setPopoverVisible(false);
    }
  };

  const handleSaveExercice = async (value) => {
    if (!value || !project?.id) return;
    const nextValue = value.format("YYYY-MM");
    setSavingExercice(true);
    try {
      await dispatch(
        updateProject({
          id: Number(project.id),
          fiscalYearEnd: nextValue,
        })
      ).unwrap();
      message.success("Exercice en cours enregistré");
      setExerciceOpen(false);
    } catch (err) {
      message.error("Impossible d'enregistrer l'exercice en cours");
    } finally {
      setSavingExercice(false);
    }
  };

  const handleSaveDefaultSyntheseRange = async (dates) => {
    if (!dates || dates.length !== 2 || !dates[0] || !dates[1] || !project?.id) {
      return;
    }

    setSavingDefaultRange(true);
    try {
      await dispatch(
        updateProject({
          id: Number(project.id),
          syntheseDateStart: dayjs(dates[0]).format("YYYY-MM"),
          syntheseDateEnd: dayjs(dates[1]).format("YYYY-MM"),
        })
      ).unwrap();
      message.success('Période de synthèse par défaut enregistrée');
      setDefaultRangeOpen(false);
      setDefaultDraftRange(null);
    } catch (err) {
      message.error("Impossible d'enregistrer la période de synthèse par défaut");
    } finally {
      setSavingDefaultRange(false);
    }
  };

  const handleExercicePeriodClick = () => {
    if (!fiscalYearEnd) {
      setRangeOpen(false);
      setDefaultRangeOpen(false);
      setExerciceOpen(true);
      return;
    }
    const { start, end } = getExerciceBounds(fiscalYearEnd);
    applySynthesePeriod([start, end]);
  };

  const handleDisplayedPeriodClick = () => {
    if (!displayedPeriodRange?.[0] || !displayedPeriodRange?.[1]) return;
    applySynthesePeriod(displayedPeriodRange);
  };

  const handleDefaultSavedClick = () => {
    if (!savedSyntheseRange) {
      setRangeOpen(false);
      setExerciceOpen(false);
      setDefaultRangeOpen(true);
      return;
    }
    applySynthesePeriod(savedSyntheseRange);
  };

  const handleExerciceValueClick = () => {
    if (fiscalYearEnd) return;
    setRangeOpen(false);
    setDefaultRangeOpen(false);
    setExerciceOpen(true);
  };

  const handleDefaultValueClick = () => {
    if (savedSyntheseRange) return;
    setRangeOpen(false);
    setExerciceOpen(false);
    setDefaultRangeOpen(true);
  };

  const monthCellRender = (value) => {
    const formattedDate = dayjs(value)
      .format("MMM")
      .replace(/\./g, "")
      .slice(0, 4);
    return <div className="ant-picker-cell-inner">{formattedDate}</div>;
  };

  const renderRangeFooter = () => (
    <>
      <Tooltip
        mouseLeaveDelay={0}
        title="Sélectionnez d'abord le mois de début de la période, puis le mois de fin"
        zIndex={1600}
        getPopupContainer={() => document.body}
      >
        <div className="period-affichee-range-info">i</div>
      </Tooltip>
      <div className="period-affichee-synthese-save-checkbox">
        <Checkbox
          checked={saveAsDefault}
          onChange={(e) => setSaveAsDefault(e.target.checked)}
        >
          Enregistrer mon choix comme "période de synthèse" par défaut
        </Checkbox>
      </div>
    </>
  );

  const renderDefaultRangeInfoFooter = () => (
    <Tooltip
      mouseLeaveDelay={0}
      title="Sélectionnez d'abord le mois de début de la période, puis le mois de fin"
      zIndex={1600}
      getPopupContainer={() => document.body}
    >
      <div className="period-affichee-range-info">i</div>
    </Tooltip>
  );

  const content = (
    <div
      ref={popoverContentRef}
      className={`period-affichee-popover ${
        mode === "compta" ? "period-affichee-popover--compta" : ""
      }`}
      style={
        valueBtnWidth
          ? { "--synthese-value-btn-width": `${valueBtnWidth}px` }
          : undefined
      }
      onClick={(e) => e.stopPropagation()}
    >
      <div className="period-affichee-popover__header">
        <h3>Sélectionner une période manuellement :</h3>
        <div className="period-affichee-picker-slot">
          <button
            ref={manualPeriodBtnRef}
            type="button"
            className="period-affichee-current-btn"
            onClick={() => {
              setExerciceOpen(false);
              setRangeOpen((prev) => !prev);
            }}
          >
            {formatMonthRange(currentSyntheseRange)}
          </button>
          <DatePicker.RangePicker
            open={rangeOpen}
            onOpenChange={(open) => {
              setRangeOpen(open);
              if (!open) {
                // Skip if onCalendarChange already applied — draftRange in this
                // closure can still be the previous range (stale state).
                if (appliedFromCalendarRef.current) {
                  appliedFromCalendarRef.current = false;
                  return;
                }
                const latest = draftRangeRef.current;
                if (latest?.[0] && latest?.[1]) {
                  applySynthesePeriod(latest, {
                    persistDefault: saveAsDefault,
                  });
                }
              }
            }}
            value={draftRange}
            onCalendarChange={(selectedDates, _dateStrings, info) => {
              setDraftRange(selectedDates);
              draftRangeRef.current = selectedDates;
              const [startDate, endDate] = selectedDates || [];
              if (startDate) {
                const year = startDate.year();
                const dropdown = document.querySelector(
                  `.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)`
                );
                const firstYear = dropdown?.querySelector(
                  ".ant-picker-year-btn"
                );
                if (firstYear) {
                  adjustCalendarYears(
                    year,
                    Number(firstYear.textContent) === year
                  );
                }
              }
              if (startDate && endDate && info?.range === "end") {
                appliedFromCalendarRef.current = true;
                applySynthesePeriod(selectedDates, {
                  persistDefault: saveAsDefault,
                });
              }
            }}
            format="MM"
            separator={null}
            locale={fr_FR}
            cellRender={monthCellRender}
            picker="month"
            placement="bottomRight"
            superNextIcon={<NextIcon />}
            superPrevIcon={<PrevIcon />}
            dropdownClassName="custom-RangePicker-dropdown period-affichee-range-dropdown period-affichee-synthese-range-dropdown"
            renderExtraFooter={renderRangeFooter}
            allowClear={false}
            className="period-affichee-hidden-picker"
            getPopupContainer={() => document.body}
          />
        </div>
      </div>

      <div className="period-affichee-block">
        <p className="period-affichee-block__title">
          Sélectionnez une période prédéfinie :
        </p>

        <div className="period-affichee-synthese-presets">
          <button
            type="button"
            className={`period-affichee-synthese-preset-btn${
              !displayedPeriodRange ? " is-disabled" : ""
            }`}
            onClick={handleDisplayedPeriodClick}
            disabled={!displayedPeriodRange}
          >
            <span className="period-affichee-synthese-preset-btn__label">
              Liée à la période affichée
            </span>
            <span className="period-affichee-current-btn period-affichee-synthese-value-btn is-readonly">
              {displayedPeriodRange
                ? formatMonthRange(displayedPeriodRange)
                : "—"}
            </span>
          </button>

          {fiscalYearEnd ? (
            <button
              type="button"
              className="period-affichee-synthese-preset-btn"
              onClick={handleExercicePeriodClick}
            >
              <span className="period-affichee-synthese-preset-btn__label">
                Liée à l&apos;exercice en cours qui clôture à la fin du mois de :
              </span>
              <span className="period-affichee-current-btn period-affichee-exercice-btn is-readonly">
                {formatFiscalYearEndLabel(fiscalYearEnd)}
              </span>
            </button>
          ) : (
            <div className="period-affichee-synthese-preset-btn">
              <button
                type="button"
                className="period-affichee-synthese-preset-btn__action"
                onClick={handleExercicePeriodClick}
              >
                Liée à l&apos;exercice en cours qui clôture à la fin du mois de :
              </button>
              <div className="period-affichee-picker-slot period-affichee-fiscal-slot">
                <button
                  type="button"
                  className="period-affichee-current-btn period-affichee-exercice-btn"
                  disabled={savingExercice}
                  onClick={handleExerciceValueClick}
                >
                  à définir
                </button>
                <DatePicker
                  open={exerciceOpen}
                  onOpenChange={setExerciceOpen}
                  value={null}
                  onChange={handleSaveExercice}
                  picker="month"
                  locale={fr_FR}
                  format="MMM YYYY"
                  allowClear={false}
                  disabled={savingExercice}
                  className="period-affichee-hidden-picker"
                  dropdownClassName="period-affichee-fiscal-dropdown"
                  panelRender={renderFiscalYearEndPickerPanel}
                  placement="bottomRight"
                  getPopupContainer={() => document.body}
                  superNextIcon={<NextIcon />}
                  superPrevIcon={<PrevIcon />}
                />
              </div>
            </div>
          )}

          {savedSyntheseRange ? (
            <button
              type="button"
              className="period-affichee-synthese-preset-btn"
              onClick={handleDefaultSavedClick}
            >
              <span className="period-affichee-synthese-preset-btn__label">
                Liée à votre période enregistrée par défaut :
              </span>
              <span className="period-affichee-current-btn period-affichee-synthese-value-btn is-readonly">
                {formatMonthRange(savedSyntheseRange)}
              </span>
            </button>
          ) : (
            <div className="period-affichee-synthese-preset-btn">
              <span className="period-affichee-synthese-preset-btn__label">
                Liée à votre période enregistrée par défaut :
              </span>
              <div className="period-affichee-picker-slot period-affichee-fiscal-slot">
                <button
                  type="button"
                  className="period-affichee-current-btn period-affichee-synthese-value-btn"
                  disabled={savingDefaultRange}
                  onClick={handleDefaultValueClick}
                >
                  à définir
                </button>
                <DatePicker.RangePicker
                  open={defaultRangeOpen}
                  onOpenChange={(open) => {
                    setDefaultRangeOpen(open);
                    if (!open && defaultDraftRange?.[0] && defaultDraftRange?.[1]) {
                      handleSaveDefaultSyntheseRange(defaultDraftRange);
                    }
                  }}
                  value={defaultDraftRange}
                  onCalendarChange={(selectedDates, _dateStrings, info) => {
                    setDefaultDraftRange(selectedDates);
                    const [startDate, endDate] = selectedDates || [];
                    if (startDate) {
                      const year = startDate.year();
                      const dropdown = document.querySelector(
                        `.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)`
                      );
                      const firstYear = dropdown?.querySelector(
                        ".ant-picker-year-btn"
                      );
                      if (firstYear) {
                        adjustCalendarYears(
                          year,
                          Number(firstYear.textContent) === year
                        );
                      }
                    }
                    if (startDate && endDate && info?.range === "end") {
                      handleSaveDefaultSyntheseRange(selectedDates);
                    }
                  }}
                  format="MM"
                  separator={null}
                  locale={fr_FR}
                  cellRender={monthCellRender}
                  picker="month"
                  placement="bottomRight"
                  superNextIcon={<NextIcon />}
                  superPrevIcon={<PrevIcon />}
                  dropdownClassName="custom-RangePicker-dropdown period-affichee-range-dropdown period-affichee-default-range-dropdown"
                  renderExtraFooter={renderDefaultRangeInfoFooter}
                  allowClear={false}
                  className="period-affichee-hidden-picker"
                  getPopupContainer={() => document.body}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const listingTooltip = isListing
    ? `Pour afficher la synthèse, vous devez passer en vue "Catégories"`
    : isGraphPage
      ? `La synthèse n'est pas disponible en vue "Graphique"`
    : isSidebarClosed
      ? buttonName
      : null;

  return (
    <div className="period-affichee-nav-item">
      <Popover
        color={mode === "treso" ? "#fffa7d" : "#a9efe5"}
        placement="right"
        align={{ offset: [12, 0] }}
        content={isDisabled ? null : content}
        visible={popoverVisible && !isDisabled}
        trigger="click"
        onVisibleChange={(visible) => {
          if (isDisabled) return;
          if (!visible && (rangeOpen || exerciceOpen || defaultRangeOpen)) return;
          setPopoverVisible(visible);
          if (!visible) {
            setRangeOpen(false);
            setExerciceOpen(false);
            setDefaultRangeOpen(false);
            setSaveAsDefault(false);
            setDefaultDraftRange(null);
          }
        }}
        overlayClassName="period-affichee-popover-overlay"
        destroyTooltipOnHide
      >
        <Tooltip
          trigger="hover"
          mouseLeaveDelay={0}
          mouseEnterDelay={0.8}
          title={listingTooltip}
        >
          <button
            type="button"
            className="button nav-link"
            style={{
              opacity: isDisabled ? 0.5 : 1,
            }}
            onClick={(e) => {
              if (isDisabled) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <img className="nav-img" src={sigmaIcon} alt="Synthèse" />
            {!isSidebarClosed && (
              <span className="hidden-element">{buttonName}</span>
            )}
          </button>
        </Tooltip>
      </Popover>
    </div>
  );
};

export default SynthesePopover;
